import React, { useEffect, useState } from "react";
import { api } from "../../utils/api";
import { FaPlus, FaTrash, FaEdit, FaSpinner, FaToggleOn, FaToggleOff } from "react-icons/fa";

const ICONS = ["FaBriefcase", "FaPlaneDeparture", "FaGraduationCap", "FaBuilding", "FaFileSignature", "FaIdCard", "FaPassport", "FaGlobe"];
const COLORS = [
    "bg-blue-50 text-blue-600", "bg-sky-50 text-sky-600", "bg-green-50 text-green-600",
    "bg-purple-50 text-purple-600", "bg-orange-50 text-orange-500", "bg-red-50 text-red-500",
];
const EMPTY = { slug: "", title: "", description: "", imageUrl: "", iconName: "FaBriefcase", color: COLORS[0], isActive: true, order: 0, features: [] };

function Modal({ title, data, onSave, onClose }) {
    const [form, setForm] = useState({ ...data, features: Array.isArray(data.features) ? data.features.join("\n") : "" });
    const [saving, setSaving] = useState(false);

    const f = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSave = async () => {
        if (!form.slug || !form.title || !form.description) return alert("Slug, Title and Description are required.");
        setSaving(true);
        try {
            await onSave({
                ...form,
                slug: form.slug.toLowerCase().replace(/\s+/g, "-"),
                features: form.features.split("\n").map(s => s.trim()).filter(Boolean),
            });
            onClose();
        } catch (err) { alert(err.message); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-lg font-bold text-slate-800">{title}</h2>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Slug * <span className="text-slate-400">(URL key)</span></label>
                            <input name="slug" value={form.slug} onChange={f} placeholder="work-visa"
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Display Order</label>
                            <input type="number" name="order" value={form.order} onChange={f}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                    </div>
                    {[
                        ["text", "title", "Title *", "e.g. Work Visa"],
                        ["text", "description", "Short Description *", "e.g. Employment & skilled worker visas..."],
                        ["text", "imageUrl", "Image URL", "https://..."],
                    ].map(([type, name, label, placeholder]) => (
                        <div key={name}>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
                            <input type={type} name={name} value={form[name]} onChange={f} placeholder={placeholder}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                    ))}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Icon</label>
                            <select name="iconName" value={form.iconName} onChange={f}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">Color Theme</label>
                            <select name="color" value={form.color} onChange={f}
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {COLORS.map(c => <option key={c} value={c}>{c.split(" ")[1].replace("text-", "")}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Features <span className="text-slate-400">(one per line)</span></label>
                        <textarea name="features" value={form.features} onChange={f} rows={4} placeholder={"Skilled Worker Nominations\nEmployer Sponsorship\n..."}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                    </div>
                </div>
                <div className="p-6 border-t border-slate-100 flex gap-3">
                    <button onClick={onClose} className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm font-medium hover:bg-slate-50 transition">Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-60">
                        {saving ? "Saving..." : "Save Service"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function AdminServices() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);

    const load = async () => {
        setLoading(true);
        const res = await api.getServices();
        setItems(res.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const handleSave = async (form) => {
        if (modal.mode === "add") {
            const res = await api.createService(form);
            setItems((prev) => [...prev, res.data].sort((a, b) => a.order - b.order));
        } else {
            const res = await api.updateService(modal.data._id, form);
            setItems((prev) => prev.map((s) => s._id === res.data._id ? res.data : s));
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await api.deleteService(id);
        setItems((prev) => prev.filter((s) => s._id !== id));
    };

    const handleToggle = async (id) => {
        const res = await api.toggleService(id);
        setItems((prev) => prev.map((s) => s._id === id ? res.data : s));
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Services</h1>
                    <p className="text-slate-500 text-sm mt-1">{items.length} services · {items.filter(s => s.isActive).length} active</p>
                </div>
                <button onClick={() => setModal({ mode: "add", data: { ...EMPTY, order: items.length + 1 } })}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition shadow-sm">
                    <FaPlus /> Add Service
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-40"><FaSpinner className="animate-spin text-blue-600 text-2xl" /></div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    {["#", "Service", "Slug", "Status", "Features", ""].map(h => (
                                        <th key={h} className="text-left px-5 py-3.5 text-slate-500 font-semibold text-xs uppercase tracking-wide">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {items.map((s) => (
                                    <tr key={s._id} className="hover:bg-slate-50 transition">
                                        <td className="px-5 py-4 text-slate-400 text-xs">{s.order}</td>
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                {s.imageUrl ? (
                                                    <img src={s.imageUrl} alt={s.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className={`w-10 h-10 rounded-lg ${s.color} flex items-center justify-center flex-shrink-0 text-sm font-bold`}>
                                                        {s.title[0]}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-semibold text-slate-800">{s.title}</p>
                                                    <p className="text-slate-400 text-xs truncate max-w-[200px]">{s.description}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4"><code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">{s.slug}</code></td>
                                        <td className="px-5 py-4">
                                            <button onClick={() => handleToggle(s._id)} className={`flex items-center gap-1.5 text-sm transition ${s.isActive ? "text-green-600" : "text-slate-400"}`}>
                                                {s.isActive ? <FaToggleOn className="text-xl" /> : <FaToggleOff className="text-xl" />}
                                                <span className="text-xs">{s.isActive ? "Active" : "Hidden"}</span>
                                            </button>
                                        </td>
                                        <td className="px-5 py-4 text-slate-400 text-xs">{s.features?.length || 0} features</td>
                                        <td className="px-5 py-4">
                                            <div className="flex gap-2">
                                                <button onClick={() => setModal({ mode: "edit", data: s })}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition"><FaEdit /></button>
                                                <button onClick={() => handleDelete(s._id)}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition"><FaTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {modal && (
                <Modal
                    title={modal.mode === "add" ? "Add Service" : "Edit Service"}
                    data={modal.data}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                />
            )}
        </div>
    );
}
